import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, TokenPair, LoginResult } from '../types';
import { authService } from '../services/authService';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  /**
   * Returns what the server decided: either the session is live, or a 2FA code
   * is still needed. Callers must branch on `status` — a challenge carries no
   * access token.
   */
  login: (email: string, password: string) => Promise<LoginResult>;
  completeMfa: (mfaToken: string, code: string) => Promise<void>;
  completeMfaWithBackupCode: (mfaToken: string, backupCode: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  logout: () => void;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (authService.isAuthenticated()) {
        try {
          const storedUser = authService.getStoredUser();
          if (storedUser) {
            setUser(storedUser);
          } else {
            const currentUser = await authService.getCurrentUser();
            setUser(currentUser);
            localStorage.setItem('user', JSON.stringify(currentUser));
          }
        } catch (error) {
          console.error('Failed to get current user:', error);
          authService.logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  /** Persist tokens and load the profile. Shared by password and 2FA logins. */
  const establishSession = async (tokens: TokenPair) => {
    if (!tokens.access_token) {
      // Defensive: never write `undefined` into storage. Doing so produced the
      // literal string "undefined" as a bearer token and surfaced as
      // "Invalid email or password" on the next request.
      throw new Error('Login did not return an access token.');
    }
    localStorage.setItem('access_token', tokens.access_token);
    if (tokens.refresh_token) {
      localStorage.setItem('refresh_token', tokens.refresh_token);
    }
    const currentUser = await authService.getCurrentUser();
    localStorage.setItem('user', JSON.stringify(currentUser));
    setUser(currentUser);
  };

  const login = async (email: string, password: string): Promise<LoginResult> => {
    const result = await authService.login({ email, password });
    if ('mfa_token' in result) {
      return { status: 'mfa_required', mfaToken: result.mfa_token };
    }
    await establishSession(result);
    return { status: 'authenticated' };
  };

  const completeMfa = async (mfaToken: string, code: string) => {
    await establishSession(await authService.verifyMfa(mfaToken, code));
  };

  const completeMfaWithBackupCode = async (mfaToken: string, backupCode: string) => {
    await establishSession(await authService.recoverMfa(mfaToken, backupCode));
  };

  /** Re-read the profile — used after enabling 2FA so gated routes re-evaluate. */
  const refreshUser = async () => {
    const currentUser = await authService.getCurrentUser();
    localStorage.setItem('user', JSON.stringify(currentUser));
    setUser(currentUser);
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  const hasRole = (role: string): boolean => user?.role === role;

  const hasAnyRole = (roles: string[]): boolean => (user ? roles.includes(user.role) : false);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        completeMfa,
        completeMfaWithBackupCode,
        refreshUser,
        logout,
        hasRole,
        hasAnyRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
